import {
  chakra,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  GridItem,
  Input,
  Switch,
  useToast,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useHasAccessTo } from "@intentsify/authorization/dist/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiService, Endpoints } from "api";
import { Button } from "components";
import { Controller, useForm } from "react-hook-form";
import { useComponentColors } from "theme";
import { SSO_PROVIDERS, SsoProvider } from "@intentsify/types";
import { z } from "zod";

const ssoConfigSchema = z.object({
  provider: z.enum(SSO_PROVIDERS),
  tenantId: z.string().min(1, "Tenant ID is required"),
  enabled: z.boolean(),
});

type SsoConfigFormValues = z.infer<typeof ssoConfigSchema>;

type SsoConfigResponse = {
  companyId: number;
  provider: SsoProvider;
  enabled: boolean;
  tenantId?: string;
} | null;

type SsoConfigProps = {
  companyId: number;
};

const SsoConfig = ({ companyId }: SsoConfigProps) => {
  const componentColors = useComponentColors();
  const toast = useToast();
  const hasAccessToDeleteSso = useHasAccessTo("company.deleteSso");
  const queryClient = useQueryClient();

  const queryKey = ["ssoConfig", companyId];

  const { data: existingSsoConfig, isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      const { data } = await apiService.get<SsoConfigResponse>(
        Endpoints.Companies.Get.SsoConfig(companyId),
      );
      return data;
    },
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<SsoConfigFormValues>({
    mode: "onBlur",
    resolver: zodResolver(ssoConfigSchema),
    values: {
      provider: SsoProvider.EntraId,
      tenantId: existingSsoConfig?.tenantId ?? "",
      enabled: existingSsoConfig?.enabled ?? true,
    },
  });

  const { mutate: save, isLoading: isSaving } = useMutation({
    mutationFn: async (values: SsoConfigFormValues) => {
      await apiService.put(
        Endpoints.Companies.Put.SsoConfig(companyId),
        values,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast({ title: "SSO configuration saved.", status: "success" });
    },
    onError: () => {
      toast({ title: "Failed to save SSO configuration.", status: "error" });
    },
  });

  const { mutate: remove, isLoading: isDeleting } = useMutation({
    mutationFn: async () => {
      await apiService.delete(Endpoints.Companies.Delete.SsoConfig(companyId));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast({ title: "SSO configuration removed.", status: "success" });
    },
    onError: () => {
      toast({ title: "Failed to remove SSO configuration.", status: "error" });
    },
  });

  if (isLoading) return null;

  return (
    <chakra.form onSubmit={handleSubmit((values) => save(values))}>
      <FormControl
        isInvalid={!!errors.tenantId}
        as={GridItem}
        isRequired
        w="300px"
      >
        <FormLabel
          color={componentColors.form.formLabelColor}
          fontSize="sm"
          fontWeight="md"
        >
          Entra ID Tenant ID
        </FormLabel>
        <Input
          {...register("tenantId")}
          placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
          shadow="sm"
          size="sm"
          rounded="md"
          autoComplete="off"
        />
        <FormErrorMessage>{errors.tenantId?.message}</FormErrorMessage>
      </FormControl>

      <FormControl as={GridItem} mt={4}>
        <FormLabel
          htmlFor="sso-enabled"
          color={componentColors.form.formLabelColor}
          fontSize="sm"
          fontWeight="md"
        >
          Enabled
        </FormLabel>
        <Controller
          name="enabled"
          control={control}
          render={({ field: { onChange, value, ref } }) => (
            <Switch
              id="sso-enabled"
              ref={ref}
              isChecked={value}
              onChange={onChange}
              colorScheme="teal"
            />
          )}
        />
      </FormControl>

      <Flex mt={4} gap={3}>
        <Button
          type="submit"
          variant="primary-teal"
          isLoading={isSaving}
          size="sm"
        >
          Save SSO Configuration
        </Button>
        {hasAccessToDeleteSso && existingSsoConfig?.companyId && (
          <Button
            variant="warning"
            size="sm"
            isLoading={isDeleting}
            onClick={() => remove()}
            type="button"
          >
            Remove SSO
          </Button>
        )}
      </Flex>
    </chakra.form>
  );
};

export { SsoConfig };
